import React, { forwardRef, KeyboardEventHandler, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { components } from "react-select";

type BasicDatePickerProps = {
    label?: string;
    value: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
};

export type BasicDatePickerHandle = {
    focus: () => void;
};


// Date picker component


const BasicDatePicker = forwardRef<BasicDatePickerHandle, BasicDatePickerProps>((props, ref) => {
    const datePickerRef = useRef<any>(null);
    const [open, setOpen] = React.useState(false);

    const focus = useCallback(() => {
        datePickerRef.current?.focus();
    }, []);

    
    useImperativeHandle(ref, () => {
        return {
            focus,
        };
    });

    const handleKeyDown: KeyboardEventHandler = (event) => {
        if ((event.code === "Space" || event.key === " ") && !open) {
            event.preventDefault();
            setOpen(true);
        }
    };
    
    

    const handleDateChange = (date: Dayjs | null) => {
        if (date === null) {
            props.onChange?.("");
            return;
        }
        if (date.isValid()) {
            props.onChange?.(date.format("YYYY-MM-DD"));
        }
        setOpen(false);
    };

    const stopPropagation = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    }, []);


    const handlePopperClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    dayjs.locale("en-gb");

    const className = useMemo(() => {
        return "h-10 w-full shadow-md rounded-md max-h-20 px-4 outline-none bg-white";
    }, []);

    return (
        <div className={`flex flex-col relative ${props.className}`}>
            {props.label != undefined && (
                <label className="mb-1">
                    {props.label}
                    {props.required && <span className="text-dark-red"> *</span>}
                </label>
            )}
                    <div onClick={stopPropagation} onKeyDown={stopPropagation}>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}
                    sx={{
                        "&": {
                            padding: 0,
                            overflow: "visible",
                            maxWidth: "100%",
                        },
                    }}>
                    <DatePicker
                        className="bg-blue"
                        format="DD/MM/YYYY"
                        ref={datePickerRef}
                        value={props.value ? dayjs(props.value) : null}
                        onChange={handleDateChange}
                        disabled={props.disabled}
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        slotProps={{
                            popper: {
                                className: "ignore-click-outside",
                                onClick: (e: React.MouseEvent) => e.stopPropagation(),
                              },
                            desktopPaper: {
                                onClick: stopPropagation,
                                onMouseDown: stopPropagation,
                            },
                            textField: {
                                size: "small",
                                className: className,
                                onKeyDown: handleKeyDown,
                                InputProps: {
                                    startAdornment: props.placeholder ? (
                                        <span className="text-grey mr-2 whitespace-nowrap">
                                            {props.placeholder}:
                                        </span>
                                    ) : undefined,
                                },
                                sx: {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: 0,
                                    },
                                    "& .MuiInputBase-input": {
                                        paddingLeft: 4,
                                    },
                                },
                            },
                            inputAdornment: {
                                position: "end"
                            }
                        }}
                    />
                </DemoContainer>
            </LocalizationProvider>
            </div>
        </div>
    );
});

BasicDatePicker.displayName = "BasicDatePicker";

export default BasicDatePicker;